import React, { useEffect, useState, useCallback } from "react";
import axios from "../api/axiosInstance";
import {
  FaMoneyBillWave,
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaChartLine,
  FaArrowRight,
  FaSyncAlt,
  FaTimes,
  FaInbox,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const VARIANT_STYLES = {
  maroon: {
    card: "bg-gradient-to-br from-[#9C2C4F] via-[#7A1A3A] to-[#5C1220] text-[#FBEFF2] shadow-[0_12px_28px_-12px_rgba(139,30,63,0.55)]",
    badge: "bg-white/15 text-[#FBEFF2]",
    title: "text-[#F3D9DE]",
    hint: "text-[#F3D9DE]/75 group-hover:text-[#F3D9DE]",
    watermark: true,
  },
  gold: {
    card: "bg-gradient-to-br from-[#E8CE85] via-[#D4A52E] to-[#B8860B] text-[#4A2E10] shadow-[0_12px_28px_-12px_rgba(184,134,11,0.5)]",
    badge: "bg-white/35 text-[#4A2E10]",
    title: "text-[#5C3D0E]",
    hint: "text-[#5C3D0E]/70 group-hover:text-[#5C3D0E]",
  },
  active: {
    card: "bg-[#FBEFF2] border border-[#E9C3CD] text-[#6E1228]",
    badge: "bg-[#F3D9DE] text-[#8B1E3F]",
    title: "text-[#A9788A]",
    hint: "text-[#A9788A] group-hover:text-[#8B1E3F]",
  },
  inactive: {
    card: "bg-[#F4F1ED] border border-[#E2DCD3] text-[#6B5E52]",
    badge: "bg-[#E9E3DA] text-[#8C7B6B]",
    title: "text-[#A6998C]",
    hint: "text-[#A6998C] group-hover:text-[#6B5E52]",
  },
};

function Dashboard() {
  const [userCount, setUserCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);

  const [todayAmount, setTodayAmount] = useState(0);
  const [weekAmount, setWeekAmount] = useState(0);
  const [monthAmount, setMonthAmount] = useState(0);
  const [yearAmount, setYearAmount] = useState(0);

  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const [modalColumns, setModalColumns] = useState([]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [users, totalAmt, status, today, week, month, year] =
        await Promise.all([
          axios.get("/api/user-count"),
          axios.get("/api/memberships/total-amount/"),
          axios.get("/api/user-status-counts/"),
          axios.get("/api/memberships/total-today-amount/"),
          axios.get("/api/memberships/total-week-amount/"),
          axios.get("/api/memberships/total-month-amount/"),
          axios.get("/api/memberships/total-year-amount/"),
        ]);

      setUserCount(users.data.total_users);
      setTotalAmount(totalAmt.data.total_amount);
      setActiveUsers(status.data.active_users);
      setInactiveUsers(status.data.inactive_users);
      setTodayAmount(today.data.total_today_amount);
      setWeekAmount(week.data.total_week_amount);
      setMonthAmount(month.data.total_month_amount);
      setYearAmount(year.data.total_year_amount);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // animation
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.07, duration: 0.45, ease: "easeOut" },
    }),
  };

  const StatCard = ({
    icon,
    title,
    value,
    badgeText,
    variant = "active",
    index,
    onClick,
  }) => {
    const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.active;
    return (
      <motion.div
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }}
        role="button"
        tabIndex={0}
        className={`group relative overflow-hidden rounded-xl p-3 sm:p-4 md:p-5 flex flex-col items-center justify-center
          text-center gap-1.5 sm:gap-2 cursor-pointer transition-all duration-300
          hover:shadow-lg hover:-translate-y-0.5
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227] focus-visible:ring-offset-2
          ${styles.card}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        custom={index}
      >
        {styles.watermark && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-7 -bottom-7 w-28 h-28 rotate-45 rounded-md border-2 border-white/10"
          />
        )}

        <div className={`relative p-2 sm:p-2.5 rounded-full ${styles.badge}`}>
          {icon}
        </div>
        <p
          className={`relative text-xs sm:text-sm font-medium ${styles.title}`}
        >
          {title}
        </p>
        <h2 className="relative text-base sm:text-xl md:text-2xl font-bold leading-tight break-words tabular-nums">
          {value}
          {badgeText && (
            <span
              className={`ml-1.5 align-middle text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded-full ${styles.badge}`}
            >
              {badgeText}
            </span>
          )}
        </h2>
        <span
          className={`relative inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium uppercase tracking-wide opacity-80 transition-opacity ${styles.hint}`}
        >
          View details <FaArrowRight size={9} />
        </span>
      </motion.div>
    );
  };

  const adminBase = "/admin-html-secure-metro";

  const openModalWith = async (title, endpoint, columns) => {
    setModalTitle(title);
    setModalColumns(columns);
    setModalLoading(true);
    setModalOpen(true);
    setModalRows([]);
    try {
      const res = await axios.get(endpoint);
      const rows = Array.isArray(res.data) ? res.data : [];
      setModalRows(rows);
    } catch (err) {
      console.error("Error fetching modal data:", err);
      setModalRows([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCardClick = (key) => {
    switch (key) {
      case "totalAmount":
        window.location.href = `${adminBase}/purchase`;
        break;
      case "totalUsers":
        window.location.href = `${adminBase}/users`;
        break;

      case "activeUsers":
        openModalWith(
          "Active Users",
          "/api/users-dashboard-table/?status=active",
          ["username", "email", "date_joined"],
        );
        break;

      case "inactiveUsers":
        openModalWith(
          "Inactive Users",
          "/api/users-dashboard-table/?status=inactive",
          ["username", "email", "date_joined"],
        );
        break;

      case "todayAmount":
        openModalWith(
          "Today's Purchases",
          "/api/memberships/purchases-dashboard-table/?period=today",
          ["username", "email", "amount", "purchased_at"],
        );
        break;
      case "weekAmount":
        openModalWith(
          "Last 7 Days Purchases",
          "/api/memberships/purchases-dashboard-table/?period=7",
          ["username", "email", "amount", "purchased_at"],
        );
        break;
      case "monthAmount":
        openModalWith(
          "Last 30 Days Purchases",
          "/api/memberships/purchases-dashboard-table/?period=30",
          ["username", "email", "amount", "purchased_at"],
        );
        break;
      case "yearAmount":
        openModalWith(
          "Last 365 Days Purchases",
          "/api/memberships/purchases-dashboard-table/?period=365",
          ["username", "email", "amount", "purchased_at"],
        );
        break;
      default:
        break;
    }
  };

  const formatCurrency = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  const totalForRatio =
    Number(activeUsers || 0) + Number(inactiveUsers || 0) || 1;
  const activePct = Math.round(
    (Number(activeUsers || 0) / totalForRatio) * 100,
  );
  const inactivePct = 100 - activePct;

  const periodData = [
    {
      key: "todayAmount",
      label: "Today",
      icon: <FaCalendarDay />,
      value: todayAmount,
    },
    {
      key: "weekAmount",
      label: "Last 7 Days",
      icon: <FaCalendarWeek />,
      value: weekAmount,
    },
    {
      key: "monthAmount",
      label: "Last 30 Days",
      icon: <FaCalendarAlt />,
      value: monthAmount,
    },
    {
      key: "yearAmount",
      label: "Last 365 Days",
      icon: <FaChartLine />,
      value: yearAmount,
    },
  ];
  const maxPeriodValue = Math.max(
    ...periodData.map((p) => Number(p.value) || 0),
    1,
  );

  const TableSkeleton = ({ columns }) => (
    <div className="p-3 sm:p-4 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-3 animate-pulse">
          {columns.map((_, j) => (
            <div key={j} className="h-3.5 flex-1 rounded bg-[#F3EADC]" />
          ))}
        </div>
      ))}
    </div>
  );

  const MiniTable = ({ columns, rows }) => {
    if (!rows || rows.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-8 text-[#A6998C]">
          <FaInbox size={22} />
          <p className="text-sm">No records found.</p>
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs sm:text-sm border-collapse">
          <thead className="bg-[#FBF3E7] sticky top-0">
            <tr>
              {columns.map((col) => (
                <th
                  className="px-2 sm:px-3 py-2 font-semibold text-[#6E1228] border-b border-[#EFE3D0] whitespace-nowrap"
                  key={col}
                >
                  {col.replace(/_/g, " ").toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className={`${
                  i % 2 === 0 ? "bg-white" : "bg-[#FFFBF2]"
                } hover:bg-[#FBEFF2]/70 transition-colors`}
              >
                {columns.map((c) => (
                  <td
                    className="px-2 sm:px-3 py-2 border-b border-[#F3EADC] text-[#5A4A3D] whitespace-nowrap"
                    key={c}
                  >
                    {r[c] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="mr-32">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-5 sm:mb-6 flex items-start sm:items-center justify-between gap-3 flex-wrap"
      >
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#4A2E1E]">
            Dashboard
          </h1>
          <div className="mt-2 h-1 w-14 rounded-full bg-gradient-to-r from-[#8B1E3F] to-[#C9A227]" />
        </div>

        <div className="flex items-center gap-3 text-xs sm:text-sm text-[#8C7B6B]">
          {lastUpdated && (
            <span className="hidden sm:inline">
              Updated{" "}
              {lastUpdated.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EFE3D0] bg-[#FFFCF7] text-[#8B1E3F] hover:bg-[#FBEFF2] transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
          >
            <FaSyncAlt className={refreshing ? "animate-spin" : ""} size={12} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Row 1: Core overview */}
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-3 sm:mb-4 lg:mb-6">
        <StatCard
          icon={<FaMoneyBillWave size={18} />}
          title="Total Amount"
          value={formatCurrency(totalAmount)}
          variant="maroon"
          index={0}
          onClick={() => handleCardClick("totalAmount")}
        />
        <StatCard
          icon={<FaUsers size={18} />}
          title="Total Users"
          value={Number(userCount || 0).toLocaleString()}
          variant="gold"
          index={1}
          onClick={() => handleCardClick("totalUsers")}
        />
        <StatCard
          icon={<FaUserCheck size={18} />}
          title="Active Users"
          value={Number(activeUsers || 0).toLocaleString()}
          badgeText={`${activePct}%`}
          variant="active"
          index={2}
          onClick={() => handleCardClick("activeUsers")}
        />
        <StatCard
          icon={<FaUserTimes size={18} />}
          title="Inactive Users"
          value={Number(inactiveUsers || 0).toLocaleString()}
          badgeText={`${inactivePct}%`}
          variant="inactive"
          index={3}
          onClick={() => handleCardClick("inactiveUsers")}
        />
      </div>

      {/* Row 2: Revenue activity by period — one grouped panel instead of four
          disconnected cards, with bars sized relative to each other so the
          shape of recent activity reads at a glance. */}
      <div className="rounded-xl border border-[#EFE3D0] bg-[#FFFCF7] shadow-sm overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-[#F3EADC] flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-[#6E1228] tracking-wide">
            Revenue Activity
          </h3>
          <span className="hidden sm:inline text-[11px] uppercase tracking-wider text-[#A6998C]">
            Tap a period for details
          </span>
        </div>
        <div className="grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#F3EADC]">
          {periodData.map((p, i) => {
            const amount = Number(p.value) || 0;
            const barWidth = Math.max((amount / maxPeriodValue) * 100, 4);
            return (
              <motion.button
                key={p.key}
                type="button"
                onClick={() => handleCardClick(p.key)}
                className="flex flex-col items-start gap-2 p-4 sm:p-5 text-left hover:bg-[#FBF3E7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227] focus-visible:ring-inset"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                custom={4 + i}
              >
                <span className="flex items-center gap-2 text-[#B8860B]">
                  <span className="text-sm">{p.icon}</span>
                  <span className="text-xs font-medium uppercase tracking-wide text-[#9C8B7A]">
                    {p.label}
                  </span>
                </span>
                <span className="text-lg sm:text-xl font-bold text-[#4A2E1E] tabular-nums">
                  {formatCurrency(amount)}
                </span>
                <span className="w-full h-1.5 rounded-full bg-[#F3EADC] overflow-hidden">
                  <span
                    className="block h-full rounded-full bg-gradient-to-r from-[#C9A227] to-[#8B1E3F] transition-all duration-500"
                    style={{ width: `${barWidth}%` }}
                  />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-[#3A2417]/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
            />

            <motion.div
              className="fixed inset-0 flex items-center justify-center z-50 p-3 sm:p-4"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
            >
              <div className="w-full max-w-2xl bg-[#FFFCF7] border border-[#EFE3D0] rounded-2xl shadow-2xl p-4 sm:p-6">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <h3 className="text-base sm:text-xl font-semibold text-[#6E1228] truncate">
                    {modalTitle}
                  </h3>
                  <button
                    onClick={() => setModalOpen(false)}
                    aria-label="Close"
                    className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full border border-[#E3B8C4] text-[#8B1E3F] hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
                  >
                    <FaTimes size={13} />
                  </button>
                </div>

                {!modalLoading && (
                  <p className="text-xs text-[#A6998C] mb-3">
                    {modalRows.length} record{modalRows.length === 1 ? "" : "s"}
                  </p>
                )}
                {modalLoading && <div className="mb-3" />}

                <div className="border border-[#EFE3D0] rounded-lg overflow-auto max-h-[70vh]">
                  {modalLoading ? (
                    <TableSkeleton columns={modalColumns} />
                  ) : (
                    <MiniTable columns={modalColumns} rows={modalRows} />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Dashboard;
